/**
 * Copyright 2022-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useLocation } from "react-router-dom";
import {
  ItemsContainer,
  ItemsSection,
  MenuItem,
  NavigationBar,
} from "@nordcloud/gnui";
import { isDev } from "~/config";
import { ROUTES } from "~/routing/routes";
import { MenuBarLink } from "./MenuItem";
import { PermissionsSubmenus } from "./submenus/PermissionsSubmenu";
import { ResourcesSubmenus } from "./submenus/ResourcesSubmenu";
import { SettingsSubmenu } from "./submenus/SettingsSubmenu";

enum MenuIcons {
  dashboard = "dashboard",
  events = "eventSchedule",
  resources = "resource",
  resourcesGroups = "organizationalStructure",
  plans = "production",
  customActions = "thunder",
  cloudAccounts = "cloud",
  notificationGroups = "bellSettings",
  permissions = "userAccess",
  settings = "settings",
  applications = "application",
  auditLogs = "eventLog",
}

type Props = {
  onExpandChange: () => void;
};

export function MenuBar({ onExpandChange }: Props) {
  const { pathname } = useLocation();

  const menuItems = [
    {
      caption: "Dashboard",
      icon: MenuIcons.dashboard,
      route: ROUTES.index,
    },
    {
      caption: "Events",
      icon: MenuIcons.events,
      route: ROUTES.events.index,
    },
    {
      caption: "Resources",
      icon: MenuIcons.resources,
      route: ROUTES.resources.index,
      children: <ResourcesSubmenus />,
      activeRoutes: [ROUTES.resources.index, ROUTES.resources.reports],
    },
    {
      caption: "Resource Groups",
      icon: MenuIcons.resourcesGroups,
      route: ROUTES.resourceGroups.index,
    },

    {
      caption: "Plans",
      icon: MenuIcons.plans,
      route: ROUTES.plans.index,
    },
    {
      caption: "Custom actions",
      icon: MenuIcons.customActions,
      route: ROUTES.customActions.index,
    },
    {
      caption: "Cloud accounts",
      icon: MenuIcons.cloudAccounts,
      route: ROUTES.accounts.index,
    },
    ...(isDev
      ? [
          {
            caption: "Applications",
            icon: MenuIcons.applications,
            route: ROUTES.applications.index,
          },
        ]
      : []),
    {
      caption: isDev ? "Settings" : "Notification Groups",
      icon: isDev ? MenuIcons.settings : MenuIcons.notificationGroups,
      route: ROUTES.notificationGroups.index,
      children: isDev ? <SettingsSubmenu /> : undefined,
      activeRoutes: [
        ROUTES.notificationGroups.index,
        ROUTES.mappingRules.index,
      ],
    },
    {
      caption: "Permissions",
      icon: MenuIcons.permissions,
      route: ROUTES.permissions.roles,
      children: <PermissionsSubmenus />,
      activeRoutes: [
        ROUTES.permissions.roles,
        ROUTES.permissions.users,
        ROUTES.permissions.apiKeys,
      ],
    },
    {
      caption: "Audit Logs",
      icon: MenuIcons.auditLogs,
      route: ROUTES.auditLogs.index,
    },
  ];

  return (
    <NavigationBar
      onExpandClick={() => {
        onExpandChange();
      }}
      popoverConfig={{
        triggerOn: "hover",
        closeOn: "hover",
        position: "end",
        adjustPositionToViewportSize: true,
      }}
    >
      <ItemsContainer>
        <ItemsSection>
          {menuItems.map((item) => {
            const isSelected = checkIsSelected(
              pathname,
              item.route,
              item.activeRoutes
            );

            return (
              <MenuBarLink
                key={item.route}
                route={item.route}
                isSelected={isSelected}
              >
                <MenuItem caption={item.caption} icon={item.icon}>
                  {item.children}
                </MenuItem>
              </MenuBarLink>
            );
          })}
        </ItemsSection>
      </ItemsContainer>
    </NavigationBar>
  );
}

const checkIsSelected = (
  pathname: string,
  path: string,
  activeRoutes?: string[]
) => {
  if (path === ROUTES.index) {
    return pathname === path;
  } else if (activeRoutes) {
    return activeRoutes.some((route) => pathname.includes(route));
  }

  return pathname.includes(path);
};
